import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { FileText, Upload, Download, CheckCircle, Clock, Loader2, DollarSign, RotateCcw, AlertCircle, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'En attente', class: 'bg-amber-100 text-amber-700', icon: Clock },
  validated: { label: 'Validee', class: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  paid: { label: 'Payee', class: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'A corriger', class: 'bg-red-100 text-red-700', icon: AlertCircle },
};

export const MyInvoices = () => {
  const { isAdmin } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [validating, setValidating] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [rejectComment, setRejectComment] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const fileInputRefs = useRef({});

  useEffect(() => {
    fetchCommissions();
  }, []);

  const fetchCommissions = async () => {
    try {
      const response = await fetch(`${API}/commissions/list`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCommissions(data);
      }
    } catch (error) {
      console.error('Failed to fetch commissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (commissionId, file) => {
    if (!file) return;
    
    setUploading(commissionId);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API}/commissions/${commissionId}/upload-invoice`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      
      if (response.ok) {
        toast.success('Facture uploadee avec succes');
        fetchCommissions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'upload');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(null);
    }
  };

  const handleDownload = async (commissionId) => {
    try {
      const response = await fetch(`${API}/commissions/${commissionId}/download-invoice`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `facture_${commissionId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        toast.error('Facture non trouvee');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur lors du telechargement');
    }
  };

  // Validate invoice (admin only) - marks as validated, NOT paid
  const handleValidateInvoice = async (commissionId) => {
    setValidating(commissionId);
    try {
      const response = await fetch(`${API}/commissions/${commissionId}/validate`, {
        method: 'PUT',
        credentials: 'include',
      });
      
      if (response.ok) {
        toast.success('Facture validee - En attente de paiement');
        fetchCommissions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      console.error('Validate error:', error);
      toast.error('Erreur');
    } finally {
      setValidating(null);
    }
  };

  const openRejectDialog = (commission) => {
    setSelectedCommission(commission);
    setRejectComment('');
    setRejectDialogOpen(true);
  };

  const handleRejectInvoice = async () => {
    if (!rejectComment.trim()) {
      toast.error('Veuillez saisir un commentaire');
      return;
    }
    
    setRejecting(true);
    try {
      const response = await fetch(`${API}/commissions/${selectedCommission.commission_id}/reject-invoice`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ comment: rejectComment }),
      });
      
      if (response.ok) {
        toast.success('Facture renvoyee au commercial');
        setRejectDialogOpen(false);
        fetchCommissions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      console.error('Reject error:', error);
      toast.error('Erreur');
    } finally {
      setRejecting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-slate-200 rounded" />
        <div className="h-64 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  // Calculate totals
  const totalCommissions = commissions.reduce((sum, c) => sum + (c.amount || 0), 0);
  const paidCommissions = commissions.filter(c => c.status === 'paid').reduce((sum, c) => sum + (c.amount || 0), 0);
  const pendingCommissions = totalCommissions - paidCommissions;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-[Manrope]" data-testid="invoices-title">
          {isAdmin ? 'Gestion des Factures' : 'Mes Factures'}
        </h1>
        <p className="text-slate-500 mt-1">
          {isAdmin 
            ? 'Validez les factures des commerciaux. Le paiement se fait dans la section Paiements.' 
            : 'Uploadez vos factures pour recevoir vos commissions'}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-indigo-100">
              <DollarSign className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total commissions</p>
              <p className="text-xl font-bold text-slate-900">{totalCommissions.toLocaleString('fr-FR', {minimumFractionDigits: 2})} EUR</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-100">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">A percevoir</p>
              <p className="text-xl font-bold text-amber-600">{pendingCommissions.toLocaleString('fr-FR', {minimumFractionDigits: 2})} EUR</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-100">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Deja paye</p>
              <p className="text-xl font-bold text-emerald-600">{paidCommissions.toLocaleString('fr-FR', {minimumFractionDigits: 2})} EUR</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info card for commercial */}
      {!isAdmin && (
        <Card className="border-0 shadow-sm bg-indigo-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-indigo-600 mt-0.5" />
              <div>
                <p className="font-medium text-slate-900">Comment ca fonctionne ?</p>
                <p className="text-sm text-slate-600 mt-1">
                  Pour chaque commission, vous devez uploader votre facture (format PDF).
                  Une fois la facture validee par l'administrateur, le paiement sera effectue le 05 du mois suivant.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info card for admin */}
      {isAdmin && (
        <Card className="border-0 shadow-sm bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-slate-900">Processus de validation</p>
                <p className="text-sm text-slate-600 mt-1">
                  1. Telechargez et verifiez la facture du commercial<br/>
                  2. <strong>Validez</strong> si elle est correcte (passe en "Validee")<br/>
                  3. <strong>Renvoyez</strong> avec un commentaire si elle doit etre corrigee<br/>
                  4. Le paiement se fait dans la section <strong>Paiements</strong>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Commissions table */}
      <Card className="border-0 shadow-sm" data-testid="commissions-table">
        <CardHeader>
          <CardTitle className="font-[Manrope]">Liste des commissions</CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Mois</TableHead>
                {isAdmin && <TableHead>Commercial</TableHead>}
                <TableHead>Vente</TableHead>
                <TableHead>Montant</TableHead>
                <TableHead>Taux</TableHead>
                <TableHead>Commission</TableHead>
                <TableHead>Facture</TableHead>
                <TableHead>Statut</TableHead>
                {!isAdmin && <TableHead>Commentaire</TableHead>}
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {commissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 9 : 10} className="text-center py-8 text-slate-500">
                    Aucune commission
                  </TableCell>
                </TableRow>
              ) : (
                commissions.map((commission) => (
                  <TableRow key={commission.commission_id} className={commission.status === 'rejected' ? 'bg-red-50' : ''}>
                    <TableCell className="font-medium">{commission.month}</TableCell>
                    {isAdmin && (
                      <TableCell>
                        <div className="text-sm">
                          <p className="font-medium">{commission.commercial_name || '-'}</p>
                          <p className="text-xs text-slate-400">{commission.commercial_email}</p>
                        </div>
                      </TableCell>
                    )}
                    <TableCell>{commission.sale_id?.slice(-8) || '-'}</TableCell>
                    <TableCell>{commission.sale_amount?.toLocaleString('fr-FR') || '-'} EUR</TableCell>
                    <TableCell>
                      <Badge variant="outline">{commission.rate}%</Badge>
                    </TableCell>
                    <TableCell className="font-semibold text-emerald-600">
                      {commission.amount?.toLocaleString('fr-FR', {minimumFractionDigits: 2})} EUR
                    </TableCell>
                    <TableCell>
                      {commission.invoice_id ? (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Uploadee
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-400">
                          Non uploadee
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={statusConfig[commission.status]?.class}>
                        {statusConfig[commission.status]?.label || commission.status}
                      </Badge>
                    </TableCell>
                    {!isAdmin && (
                      <TableCell>
                        {commission.rejection_comment && (
                          <div className="max-w-[200px]">
                            <p className="text-sm text-red-600 font-medium">
                              {commission.rejection_comment}
                            </p>
                          </div>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {/* Upload button for commercial only - not paid and not validated */}
                        {!isAdmin && commission.status !== 'paid' && commission.status !== 'validated' && (
                          <>
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              className="hidden"
                              ref={(el) => fileInputRefs.current[commission.commission_id] = el}
                              onChange={(e) => handleUpload(commission.commission_id, e.target.files[0])}
                            />
                            <Button
                              variant={commission.status === 'rejected' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => fileInputRefs.current[commission.commission_id]?.click()}
                              disabled={uploading === commission.commission_id}
                              className={commission.status === 'rejected' ? 'bg-red-600 hover:bg-red-700' : ''}
                              title={commission.status === 'rejected' ? 'Uploader nouvelle facture' : 'Uploader facture'}
                            >
                              {uploading === commission.commission_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Upload className="w-4 h-4" />
                              )}
                            </Button>
                          </>
                        )}
                        
                        {/* Download button - visible if invoice exists */}
                        {commission.invoice_id && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownload(commission.commission_id)}
                            title="Telecharger la facture"
                            className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {/* Admin actions for invoices - Validate or Reject */}
                        {isAdmin && commission.invoice_id && (commission.status === 'pending' || commission.status === 'rejected') && (
                          <>
                            {/* Validate button */}
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleValidateInvoice(commission.commission_id)}
                              disabled={validating === commission.commission_id}
                              className="bg-blue-600 hover:bg-blue-700"
                              title="Valider la facture"
                            >
                              {validating === commission.commission_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Check className="w-4 h-4" />
                              )}
                            </Button>
                            
                            {/* Reject/Return button */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openRejectDialog(commission)}
                              className="text-amber-600 border-amber-300 hover:bg-amber-50"
                              title="Renvoyer avec commentaire"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        
                        {/* Show validated status for admin */}
                        {isAdmin && commission.status === 'validated' && (
                          <span className="text-sm text-blue-600 font-medium">
                            En attente de paiement
                          </span>
                        )}
                        
                        {/* Show paid status */}
                        {commission.status === 'paid' && (
                          <span className="text-sm text-emerald-600 font-medium">
                            Paye
                          </span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renvoyer la facture</DialogTitle>
            <DialogDescription>
              La facture sera renvoyee au commercial avec votre commentaire. Il pourra ensuite uploader une version corrigee.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reject-comment">Commentaire / Raison du renvoi</Label>
              <Textarea
                id="reject-comment"
                placeholder="Ex: Veuillez corriger le montant TTC, il ne correspond pas..."
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                rows={4}
              />
            </div>
            {selectedCommission && (
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p><span className="font-medium">Commercial:</span> {selectedCommission.commercial_name}</p>
                <p><span className="font-medium">Commission:</span> {selectedCommission.amount?.toLocaleString('fr-FR')} EUR</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleRejectInvoice} 
              disabled={rejecting}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {rejecting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Renvoyer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
